import { getStaticDashboardConfig } from '@/hooks/useDashboardConfig';
import { createHttpHeader } from '@/utils/network/createHTTPHeaders';
import { ITaxResidence, ITaxResidenceBase } from '@/store/models/taxResidence.model';
import { decryptTreezorResponse } from '@/services/encryption/decryptTreezorResponse';
import { fetchQuery } from '@/utils/queryClient';
import { fetchMethods } from '@/types/FetchService.types';

export class TaxResidenceService {
  static queryKey = 'Tax Residences';
  static getTaxResidences = async (userId: number): Promise<ITaxResidence[]> => {
    try {
      const config = getStaticDashboardConfig();
      const headers = createHttpHeader();
      const url = `${config.api.baseUrl}/v1/taxResidences?userId=${userId}&pageCount=50`;

      const response = await fetchQuery([this.queryKey, { method: fetchMethods.GET, user: userId }], url, { headers });

      return await decryptTreezorResponse<ITaxResidence>(response, { shouldSkipEncryption: true });
    } catch (error) {
      return Promise.reject(error);
    }
  };

  static addTaxResidence = async (taxResidence: ITaxResidenceBase, batchIndex?: string) => {
    try {
      const config = getStaticDashboardConfig();
      const headers = createHttpHeader();
      const url = `${config.api.baseUrl}/v1/taxResidences`;

      const queryKey = batchIndex ? `${this.queryKey}-${batchIndex}` : this.queryKey;

      const response = await fetchQuery(
        [queryKey, { method: fetchMethods.POST, id: taxResidence.id, params: { taxResidence } }],
        url,
        {
          headers,
          method: 'POST',
          body: JSON.stringify({ ...taxResidence }),
        },
      );

      const { taxResidences } = await decryptTreezorResponse<{ taxResidences: ITaxResidence[] }>(response, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
      return taxResidences[0];
    } catch (error) {
      return Promise.reject(error);
    }
  };

  static updateTaxResidence = async ({ id, country, taxPayerId, liabilityWaiver }: ITaxResidenceBase) => {
    try {
      const config = getStaticDashboardConfig();
      const headers = createHttpHeader();
      const url = `${config.api.baseUrl}/v1/taxResidences/${id}`;

      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.PUT, user: id, params: { country, taxPayerId, liabilityWaiver } }],
        url,
        {
          headers,
          body: JSON.stringify({ country, taxPayerId, liabilityWaiver }),
        },
      );

      const { taxResidences } = await decryptTreezorResponse<{ taxResidences: ITaxResidence[] }>(response, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
      return taxResidences[0];
    } catch (error) {
      return Promise.reject(error);
    }
  };

  static deleteTaxResidence = async (taxResidenceId: number) => {
    try {
      const config = getStaticDashboardConfig();
      const headers = createHttpHeader();
      const url = `${config.api.baseUrl}/v1/taxResidences/${taxResidenceId}`;

      const response = await fetchQuery([this.queryKey, { method: fetchMethods.DELETE, id: taxResidenceId }], url, {
        headers,
      });

      return await decryptTreezorResponse<ITaxResidence>(response, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (error) {
      return Promise.reject(error);
    }
  };
}
